import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type { Message } from '../types';

const MOCK_MESSAGES: Message[] = [
  { id: '1', conversation_id: 'mock', direction: 'outbound', sender_type: 'ai_agent', body: "Hi Sarah! 👋 I noticed TechCorp recently expanded its sales team. We've helped similar companies automate their outreach and book 3x more meetings. Would you be open to a quick chat?", status: 'read', created_at: '2026-03-10T10:24:00Z' },
  { id: '2', conversation_id: 'mock', direction: 'inbound', sender_type: 'contact', body: 'Hi! How did you find me?', status: 'read', created_at: '2026-03-10T10:31:00Z' },
  { id: '3', conversation_id: 'mock', direction: 'outbound', sender_type: 'ai_agent', body: "We found your profile through LinkedIn — your work on scaling TechCorp's revenue team is impressive! We specifically help sales leaders like you streamline prospecting. Our clients typically see a 40% reduction in time spent on manual outreach.", status: 'read', created_at: '2026-03-10T10:32:00Z' },
  { id: '4', conversation_id: 'mock', direction: 'inbound', sender_type: 'contact', body: 'That sounds interesting! Can we schedule a call?', status: 'read', created_at: '2026-03-10T10:45:00Z' },
];

export function useRealtimeMessages(conversationId: string | null): {
  messages: Message[];
  loading: boolean;
  error: string | null;
} {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      return;
    }

    if (!isSupabaseConfigured) {
      setMessages(MOCK_MESSAGES);
      return;
    }

    let cancelled = false;

    const loadMessages = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from('messages')
          .select('*')
          .eq('conversation_id', conversationId)
          .order('created_at');
        if (cancelled) return;
        if (err) {
          setError(err.message);
        } else {
          setMessages((data as Message[]) ?? []);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadMessages();

    const channel = supabase
      .channel('messages:' + conversationId)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          if (!cancelled) {
            setMessages((prev) => [...prev, payload.new as Message]);
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [conversationId]);

  return { messages, loading, error };
}
